import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Stack, CircularProgress, Alert, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, Tabs, Tab, Tooltip, Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete,
  Select, MenuItem, FormControl, InputLabel, FormControlLabel, FormHelperText, Switch,
  InputAdornment, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import ArrowRightAltRounded from '@mui/icons-material/ArrowRightAltRounded';
import LocalPrintshopRounded from '@mui/icons-material/LocalPrintshopRounded';
import KeyboardArrowRightRounded from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import QrCode2Rounded from '@mui/icons-material/QrCode2Rounded';

import {
  getInkStock, InkStockRow, InkStockReport,
  getInkStockDetail, InkStockDetail, InkTubRow, InkLedgerRow, getBatchPailLabels,
  listInkSuppliers, InkSupplier, upsertInkSupplier, deleteInkSupplier, InkSupplierUpsert,
  createInkReceipt, InkReceiptLine,
  createInkOpeningBalance, createInkAdjustment,
  listInkProducts, InkProduct,
  InkBatch,
} from './productionApi';
import { productionKeys } from './queryKeys';
import { TOKENS } from './board/ganttConfig';

const LMQ_RED = '#d32f2f';
const TROLLEY_BLUE = '#2563eb';   // stock that's out on a trolley — still ours
const COATING_HEX = '#8C9BA5';

/** Everything is stored ALL CAPS — force it on entry regardless of caps lock. */
const up = (s: string) => s.toUpperCase();

/** Money, always 2dp. */
const money = (v: number | null | undefined) =>
  v == null ? '—' : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Weights, up to 3dp with trailing zeros stripped. */
const kg = (v: number | null | undefined) =>
  v == null ? '—' : `${v.toLocaleString(undefined, { maximumFractionDigits: 3 })} kg`;

const dateOf = (iso: string | null | undefined) =>
  !iso ? '—' : new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });

const dateTimeOf = (iso: string | null | undefined) =>
  !iso ? '—' : new Date(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });

/**
 * Backend errors arrive as `{success:false, error:"..."}` on 400/409. The 409 on
 * insufficient stock carries a genuinely useful message ("Not enough CHECO at
 * STORE: asked for 50 kg, only 43 kg on hand") — surface it verbatim.
 */
function apiError(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e?.response?.data?.error ?? e?.message ?? 'Something went wrong.';
}

const num = (s: string): number => {
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

function Loading() {
  return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>;
}

/** The ink's real colour swatch (coatings have no CMYK colour — uniform varnish tint). */
export function Swatch({ hex, coating, size = 14 }: { hex?: string | null; coating?: boolean; size?: number }) {
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '3px', flexShrink: 0,
      bgcolor: hex ?? (coating ? COATING_HEX : '#ccc'),
      border: '1px solid rgba(0,0,0,0.2)',
    }} />
  );
}

/** Series colour-code chip — the same display_hex tag used across the app. */
export function SeriesTag({ row }: {
  row: { manufacturer?: string; series_name?: string; ink_system?: string; series_hex?: string | null };
}) {
  const label = [row.manufacturer, row.series_name, row.ink_system].filter(Boolean).join(' ').toUpperCase();
  const hex = row.series_hex ?? '#999';
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.75, py: '1px',
      borderRadius: '5px', fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
      border: `1px solid ${hex}`, borderLeft: `4px solid ${hex}`,
      bgcolor: `${hex}14`, color: TOKENS.ink,
    }}>
      {label || '—'}
    </Box>
  );
}

// ---------------------------------------------------------------------------

/**
 * InkStockView — the ink store, held as an append-only ledger in kg.
 *
 * The model's one idea worth internalising: quantities are always POSITIVE and
 * direction lives in from_location → to_location, where null means "outside the
 * business". So ink kitted out onto a gang-run trolley is a TRANSFER — it is
 * STILL OURS — and value only ever leaves on a `depletion`. The table therefore
 * splits on-hand into STORE and ON TROLLEY rather than hiding the difference.
 */
/**
 * Standalone this owns its page header and its own Stock/Suppliers tabs. Inside
 * the Ink shell (InkMaterialsView) the shell owns both, so it passes `embedded`
 * to drop them and `section` to pin which half to render — tabs nested inside
 * tabs would just be noise.
 */
export default function InkStockView({ embedded, section }: {
  embedded?: boolean;
  section?: 'stock' | 'suppliers';
} = {}) {
  const qc = useQueryClient();
  const [ownTab, setOwnTab] = useState<'stock' | 'suppliers'>('stock');
  const tab = section ?? ownTab;
  const setTab = setOwnTab;

  const [search, setSearch] = useState('');
  const [materialClass, setMaterialClass] = useState<'ink' | 'coating' | 'all'>('all');
  const [belowReorder, setBelowReorder] = useState(false);

  const [detailKey, setDetailKey] = useState<string | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);
  const [adjustRow, setAdjustRow] = useState<InkStockRow | null>(null);

  const stockParams = { materialClass, search, belowReorder };
  const stockQ = useQuery<InkStockReport>({
    queryKey: productionKeys.inkStock(stockParams),
    queryFn: () => getInkStock(stockParams),
    staleTime: 30 * 1000,
  });

  /** A receipt/adjustment/opening moves the roll-up, the batches AND every detail. */
  const invalidateLedger = () => {
    qc.invalidateQueries({ queryKey: ['production', 'ink-stock'] });   // roll-up + all details
    qc.invalidateQueries({ queryKey: ['production', 'ink-batches'] });
    qc.invalidateQueries({ queryKey: ['production', 'ink-ledger'] });
  };

  // Below-reorder rows float to the top — that's the thing you act on.
  const rows = useMemo(() => {
    const list = [...(stockQ.data?.stock ?? [])];
    list.sort((a, b) => {
      if (a.below_reorder_point !== b.below_reorder_point) return a.below_reorder_point ? -1 : 1;
      return a.ink_key.localeCompare(b.ink_key);
    });
    return list;
  }, [stockQ.data?.stock]);

  const totalValue = stockQ.data?.total_stock_value ?? 0;
  const lowCount = stockQ.data?.below_reorder_count ?? 0;
  const trolleyTotal = useMemo(() => rows.reduce((t, r) => t + (r.trolley_kg || 0), 0), [rows]);

  return (
    <Box sx={embedded ? { p: 0 } : { p: { xs: 1.5, md: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
      {!embedded && (
        <>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
            <Inventory2Rounded sx={{ color: TOKENS.accent }} />
            <Typography sx={{ fontWeight: 700, fontSize: 20 }}>Ink stock</Typography>
          </Stack>
          <Typography sx={{ fontSize: 13, color: TOKENS.ink3, mb: 2 }}>
            An append-only ledger in kg, valued at what we actually paid (FIFO batch layers).
            Ink kitted out onto a trolley is still ours — it only leaves the business when a run is
            reconciled.
          </Typography>

          <Tabs value={tab} onChange={(_e, v: 'stock' | 'suppliers') => setTab(v)}
            sx={{ mb: 2, minHeight: 36, borderBottom: '1px solid rgba(0,0,0,0.08)',
              '& .MuiTab-root': { minHeight: 36, fontSize: 13, fontWeight: 700, textTransform: 'none' },
              '& .Mui-selected': { color: `${TOKENS.accent} !important` },
              '& .MuiTabs-indicator': { bgcolor: TOKENS.accent } }}>
            <Tab value="stock" label="Stock on hand" />
            <Tab value="suppliers" label="Suppliers" />
          </Tabs>
        </>
      )}

      {tab === 'suppliers' ? (
        <SuppliersSection />
      ) : (
        <>
          {/* ---------- headline: value, trolley, below-reorder ---------- */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap',
            gap: 2, p: 2, mb: 2, borderRadius: '12px', bgcolor: TOKENS.lineSoft,
          }}>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              <Box>
                <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3 }}>
                  Stock value
                </Typography>
                <Typography sx={{ fontSize: 30, fontWeight: 800, color: TOKENS.accent, lineHeight: 1.15 }}>
                  {money(totalValue)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
                  {rows.length} SKU{rows.length === 1 ? '' : 's'} · at actual batch cost
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3 }}>
                  Out on trolleys
                </Typography>
                <Typography sx={{ fontSize: 30, fontWeight: 800, color: TROLLEY_BLUE, lineHeight: 1.15 }}>
                  {kg(trolleyTotal)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>Still ours — not consumed</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: TOKENS.ink3 }}>
                  Below reorder
                </Typography>
                <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15, color: lowCount ? TOKENS.bad : TOKENS.good }}>
                  {lowCount}
                </Typography>
                <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
                  {lowCount ? 'need ordering' : 'all above reorder point'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" variant="outlined" startIcon={<AddRounded />}
                onClick={() => setOpeningOpen(true)}
                sx={{ color: TOKENS.accent, borderColor: `${TOKENS.accent}80` }}>
                Opening balance
              </Button>
              <Button size="small" variant="contained" startIcon={<AddRounded />}
                onClick={() => setReceiptOpen(true)}
                sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
                Goods in
              </Button>
            </Stack>
          </Box>

          {/* ---------- filters ---------- */}
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
            <TextField size="small" placeholder="Search key / description / colour…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded fontSize="small" /></InputAdornment> }}
              sx={{ width: 280 }} />
            <ToggleButtonGroup size="small" exclusive value={materialClass}
              onChange={(_e, v: 'ink' | 'coating' | 'all' | null) => { if (v) setMaterialClass(v); }}>
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.5 }}>All</ToggleButton>
              <ToggleButton value="ink" sx={{ textTransform: 'none', px: 1.5 }}>Inks</ToggleButton>
              <ToggleButton value="coating" sx={{ textTransform: 'none', px: 1.5 }}>Coatings</ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel
              control={<Switch size="small" checked={belowReorder} onChange={(e) => setBelowReorder(e.target.checked)} />}
              label={<Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>Below reorder only</Typography>} />
          </Stack>

          {/* ---------- the stock table ---------- */}
          {stockQ.isLoading ? <Loading /> : stockQ.isError ? (
            <Alert severity="error">Couldn’t load ink stock: {apiError(stockQ.error)}</Alert>
          ) : (
            <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
                <TableHead>
                  <TableRow>
                    {['Ink', 'Description', 'Series', 'Supplier'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>{h}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Store</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TROLLEY_BLUE }}>On trolley</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>On hand</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Value</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>Reorder pt</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const coating = r.material_class === 'coating';
                    return (
                      <TableRow key={r.ink_key} hover onClick={() => setDetailKey(r.ink_key)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: r.below_reorder_point ? 'rgba(220,38,38,0.05)' : undefined,
                          '& > td:first-of-type': r.below_reorder_point
                            ? { boxShadow: `inset 3px 0 0 ${TOKENS.bad}` } : undefined,
                        }}>
                        <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, whiteSpace: 'nowrap' }}>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Swatch hex={r.hex_colour} coating={coating} />
                            <Box component="span" sx={{ color: r.low_migration ? LMQ_RED : TOKENS.ink }}>{r.ink_key}</Box>
                            {r.low_migration && (
                              <Chip size="small" label="LMQ" sx={{ height: 16, fontSize: 9.5, fontWeight: 800, color: '#fff', bgcolor: LMQ_RED }} />
                            )}
                            {r.below_reorder_point && (
                              <Tooltip title={`On hand ${kg(r.on_hand_kg)} ≤ reorder point ${kg(r.reorder_point_kg)}`}>
                                <Chip size="small" icon={<WarningAmberRounded sx={{ fontSize: 12, color: '#fff !important' }} />}
                                  label="LOW" sx={{ height: 16, fontSize: 9.5, fontWeight: 800, color: '#fff', bgcolor: TOKENS.bad,
                                    '& .MuiChip-icon': { ml: '4px', mr: '-4px' } }} />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: r.low_migration ? LMQ_RED : TOKENS.ink2 }}>
                          {r.description?.toUpperCase()}
                        </TableCell>
                        <TableCell><SeriesTag row={r} /></TableCell>
                        <TableCell sx={{ fontSize: 12, color: TOKENS.ink2, whiteSpace: 'nowrap' }}>
                          {r.supplier_code ?? <Box component="span" sx={{ color: TOKENS.ink3 }}>—</Box>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>
                          {kg(r.store_kg)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5 }}>
                          {r.trolley_kg > 0 ? (
                            <Chip size="small" label={kg(r.trolley_kg)}
                              sx={{ height: 19, fontSize: 11, fontWeight: 700, color: TROLLEY_BLUE,
                                bgcolor: `${TROLLEY_BLUE}14`, border: `1px solid ${TROLLEY_BLUE}55` }} />
                          ) : <Box component="span" sx={{ color: TOKENS.ink3 }}>—</Box>}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, fontSize: 13 }}>
                          {kg(r.on_hand_kg)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12.5, color: TOKENS.ink2 }}>
                          {money(r.stock_value)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 12, color: TOKENS.ink3 }}>
                          {r.reorder_point_kg == null ? '—' : kg(r.reorder_point_kg)}
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Adjust (stock take, spoilage, spillage)">
                            <IconButton size="small" onClick={() => setAdjustRow(r)}><TuneRounded fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={10} sx={{ color: TOKENS.ink3, textAlign: 'center', py: 3 }}>
                      {belowReorder
                        ? 'Nothing is below its reorder point.'
                        : 'No stock yet — load what’s already on the shelf with “Opening balance”.'}
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}

      {detailKey && (
        <DetailDialog inkKey={detailKey} onClose={() => setDetailKey(null)}
          onAdjust={(row) => { setDetailKey(null); setAdjustRow(row); }} />
      )}
      {receiptOpen && (
        <LinesDialog mode="receipt" onClose={() => setReceiptOpen(false)}
          onSaved={() => { setReceiptOpen(false); invalidateLedger(); }} />
      )}
      {openingOpen && (
        <LinesDialog mode="opening" onClose={() => setOpeningOpen(false)}
          onSaved={() => { setOpeningOpen(false); invalidateLedger(); }} />
      )}
      {adjustRow && (
        <AdjustDialog row={adjustRow} onClose={() => setAdjustRow(null)}
          onSaved={() => { setAdjustRow(null); invalidateLedger(); }} />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Detail — open batches, where it is, and the ledger history.
// ---------------------------------------------------------------------------

/** `depletion` (to_location null) is the ONLY row where value leaves the business. */
const TXN_STYLE: Record<string, { label: string; colour: string }> = {
  opening: { label: 'OPENING', colour: TOKENS.ink3 },
  receipt: { label: 'RECEIPT', colour: TOKENS.good },
  issue: { label: 'ISSUE', colour: TROLLEY_BLUE },
  return: { label: 'RETURN', colour: TROLLEY_BLUE },
  depletion: { label: 'DEPLETION', colour: TOKENS.bad },
  adjustment: { label: 'ADJUSTMENT', colour: TOKENS.warn },
};

export function DetailDialog({ inkKey, onClose, onAdjust }: {
  inkKey: string; onClose: () => void; onAdjust: (row: InkStockRow) => void;
}) {
  const q = useQuery<InkStockDetail>({
    queryKey: productionKeys.inkStockDetail(inkKey),
    queryFn: () => getInkStockDetail(inkKey),
    staleTime: 30 * 1000,
  });
  const d = q.data;
  const ink = d?.ink;

  // Per-pail labels: fetch the ZPL and download it as a .zpl the LAN PC sends to
  // the Zebra (manual nc for now; the same action becomes one-click once the
  // local print bridge lands).
  const [printing, setPrinting] = useState<string | null>(null);
  const [labelErr, setLabelErr] = useState<string | null>(null);
  const printLabels = async (batchNo: string) => {
    setPrinting(batchNo);
    setLabelErr(null);
    try {
      const zpl = await getBatchPailLabels(batchNo);
      const url = URL.createObjectURL(new Blob([zpl], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batchNo}-pails.zpl`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setLabelErr(apiError(e));
    } finally {
      setPrinting(null);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Swatch hex={ink?.hex_colour} coating={ink?.material_class === 'coating'} size={18} />
          <Box component="span" sx={{ fontFamily: TOKENS.monoFont, color: ink?.low_migration ? LMQ_RED : TOKENS.ink }}>
            {inkKey}
          </Box>
          <Typography sx={{ fontSize: 13, color: TOKENS.ink3, flexGrow: 1 }}>
            {ink?.description?.toUpperCase() ?? ''}
          </Typography>
          {ink && (
            <Button size="small" startIcon={<TuneRounded />} onClick={() => onAdjust(ink)} sx={{ color: TOKENS.accent }}>
              Adjust
            </Button>
          )}
          <IconButton size="small" onClick={onClose}><CloseRounded fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {q.isLoading ? <Loading /> : q.isError ? (
          <Alert severity="error">Couldn’t load {inkKey}: {apiError(q.error)}</Alert>
        ) : d && ink ? (
          <Stack spacing={2.5}>
            {/* where it is */}
            <Box>
              <SectionLabel>Where it is</SectionLabel>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {d.locations.length === 0 && (
                  <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>Nothing on hand.</Typography>
                )}
                {d.locations.map((l) => {
                  const trolley = l.location_type === 'trolley';
                  const c = trolley ? TROLLEY_BLUE : TOKENS.ink2;
                  return (
                    <Box key={l.location} sx={{
                      px: 1.25, py: 0.75, borderRadius: '8px', minWidth: 120,
                      border: `1px solid ${c}44`, bgcolor: `${c}0F`,
                    }}>
                      <Typography sx={{ fontFamily: TOKENS.monoFont, fontSize: 11.5, fontWeight: 700, color: c }}>
                        {l.location}
                      </Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{kg(l.on_hand_kg)}</Typography>
                      {trolley && (
                        <Typography sx={{ fontSize: 10, color: TROLLEY_BLUE }}>still ours</Typography>
                      )}
                    </Box>
                  );
                })}
                <Box sx={{ px: 1.25, py: 0.75, borderRadius: '8px', minWidth: 120, bgcolor: TOKENS.lineSoft }}>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: TOKENS.ink3 }}>TOTAL VALUE</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: TOKENS.accent }}>{money(ink.stock_value)}</Typography>
                  <Typography sx={{ fontSize: 10, color: TOKENS.ink3 }}>{kg(ink.on_hand_kg)} on hand</Typography>
                </Box>
              </Stack>
            </Box>

            {/* open batches */}
            <Box>
              <SectionLabel>Open batches — ours ↔ theirs</SectionLabel>
              {labelErr && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setLabelErr(null)}>{labelErr}</Alert>}
              <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
                <TableHead>
                  <TableRow>
                    {['Our batch', 'Their batch', 'Received', 'Remaining', 'Unit cost', 'Value'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: TOKENS.ink3 }}>{h}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11, color: TOKENS.ink3 }}>Labels</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {d.batches.map((b: InkBatch) => (
                    <TableRow key={b.batch_id} hover>
                      <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 12 }}>{b.batch_no}</TableCell>
                      <TableCell sx={{ fontFamily: TOKENS.monoFont, fontSize: 12, color: TOKENS.ink2 }}>
                        {b.supplier_batch_code ?? '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: TOKENS.ink2 }}>{dateOf(b.received_at)}</TableCell>
                      <TableCell sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {kg(b.remaining_kg)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: TOKENS.ink2 }}>
                        {b.unit_cost_per_kg == null ? '—' : `${money(b.unit_cost_per_kg)}/kg`}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>{money(b.remaining_value)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download per-pail labels (.zpl) — send to the Zebra on the LAN">
                          <span>
                            <IconButton size="small" disabled={printing === b.batch_no}
                              onClick={() => printLabels(b.batch_no)}>
                              {printing === b.batch_no
                                ? <CircularProgress size={15} />
                                : <LocalPrintshopRounded fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {d.batches.length === 0 && (
                    <TableRow><TableCell colSpan={7} sx={{ color: TOKENS.ink3, textAlign: 'center', py: 2 }}>
                      No open batches.
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            {/* pots on hand — the sealed tubs physically present, drillable to barcode */}
            <PotsOnHand tubs={d.tubs} />
            {/* opened tubs — each with the job it was scanned onto at the press (or
                the run only, when nobody scanned it), and its supplier batch */}
            <OpenedTubs tubs={d.tubs} />

            {/* recent history — a running In / Out / Available ledger */}
            <RunningLedger ledger={d.ledger} onHandKg={ink.on_hand_kg} />
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{
      fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.5,
      fontWeight: 800, color: TOKENS.ink3, mb: 0.75,
    }}>
      {children}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// Pots on hand — the sealed tubs physically present right now, each with its
// barcode. A plain collapsible listing (not the whole history): what you'd
// count if you walked the shelf. This is the grain on-hand kg sums up from.
// ---------------------------------------------------------------------------

const OWNER_STYLE: Record<string, { label: string; colour: string }> = {
  owned: { label: 'OURS', colour: TOKENS.accent },
  consigned: { label: 'CONSIGNED', colour: TROLLEY_BLUE },
};

function TinyChip({ style }: { style: { label: string; colour: string } }) {
  return (
    <Chip size="small" label={style.label} sx={{
      height: 17, fontSize: 9, fontWeight: 800, color: style.colour,
      bgcolor: `${style.colour}1F`, border: `1px solid ${style.colour}55`,
      '& .MuiChip-label': { px: 0.75 },
    }} />
  );
}

// Opened tubs — the traceability view (roadmap session 2). A job on a tub means
// exactly one thing: it was scanned at the press against that job, which ties
// this supplier batch to that job. A tub with a run but no job was opened
// without a press scan — the gap settlement reports.
function OpenedTubs({ tubs }: { tubs: InkTubRow[] }) {
  const opened = useMemo(
    () => tubs.filter((t) => t.status === 'opened')
      .sort((a, b) => (b.opened_at ?? '').localeCompare(a.opened_at ?? '')),
    [tubs],
  );
  const [open, setOpen] = useState(false);
  const unscanned = opened.filter((t) => !t.job_number).length;
  if (opened.length === 0) return null;
  const fmtWhen = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : '—';

  return (
    <Box>
      <SectionLabel>Opened tubs</SectionLabel>
      <Box sx={{ border: `1px solid ${TOKENS.line}`, borderRadius: '8px', overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" spacing={1}
          onClick={() => setOpen((v) => !v)}
          sx={{ px: 1, py: 0.85, cursor: 'pointer', bgcolor: TOKENS.lineSoft, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}>
          {open
            ? <KeyboardArrowDownRounded sx={{ fontSize: 18, color: TOKENS.ink3 }} />
            : <KeyboardArrowRightRounded sx={{ fontSize: 18, color: TOKENS.ink3 }} />}
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink }}>
            {opened.length} opened {opened.length === 1 ? 'tub' : 'tubs'}
          </Typography>
          {unscanned > 0 && (
            <Typography sx={{ fontSize: 11.5, color: TOKENS.warn, fontWeight: 700 }}>
              · {unscanned} without a press scan
            </Typography>
          )}
        </Stack>
        <Collapse in={open} unmountOnExit>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
            <TableHead>
              <TableRow>
                {['Barcode', 'Job', 'Run', 'Supplier batch', 'Opened'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 10.5, color: TOKENS.ink3, py: 0.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {opened.map((t) => (
                <TableRow key={t.unit_code} hover>
                  <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 11.5, py: 0.5 }}>{t.unit_code}</TableCell>
                  <TableCell sx={{ fontFamily: TOKENS.monoFont, fontSize: 11.5, py: 0.5,
                    color: t.job_number ? TOKENS.ink : TOKENS.warn, fontWeight: t.job_number ? 700 : 500 }}>
                    {t.job_number ?? 'not scanned'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink2, py: 0.5 }}>
                    {t.trolley_run_id != null ? `#${t.trolley_run_id}` : '—'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink2, py: 0.5, whiteSpace: 'nowrap' }}>
                    {t.batch_no ?? '—'}
                    {t.supplier_batch_code && (
                      <Box component="span" sx={{ color: TOKENS.ink3 }}> / {t.supplier_batch_code}</Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 11.5, color: TOKENS.ink2, py: 0.5, whiteSpace: 'nowrap' }}>
                    {fmtWhen(t.opened_at)}{t.opened_by ? ` · ${t.opened_by}` : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </Box>
    </Box>
  );
}

function PotsOnHand({ tubs }: { tubs: InkTubRow[] }) {
  // On hand = the sealed tubs physically present (opened tubs are consumed; a
  // written-off tub is gone). A sealed tub out on a trolley is still ours and
  // still on hand — the location column shows where each one sits.
  const onHand = useMemo(
    () => tubs.filter((t) => t.status === 'sealed')
      .sort((a, b) => (a.batch_no ?? '').localeCompare(b.batch_no ?? '') || a.unit_code.localeCompare(b.unit_code)),
    [tubs],
  );
  const totalKg = useMemo(() => onHand.reduce((s, t) => s + t.nominal_kg, 0), [onHand]);
  const [open, setOpen] = useState(true);

  return (
    <Box>
      <SectionLabel>Pots on hand</SectionLabel>
      <Box sx={{ border: `1px solid ${TOKENS.line}`, borderRadius: '8px', overflow: 'hidden' }}>
        <Stack direction="row" alignItems="center" spacing={1}
          onClick={() => onHand.length > 0 && setOpen((v) => !v)}
          sx={{ px: 1, py: 0.85, cursor: onHand.length ? 'pointer' : 'default', bgcolor: TOKENS.lineSoft,
            '&:hover': { bgcolor: onHand.length ? 'rgba(0,0,0,0.04)' : TOKENS.lineSoft } }}>
          {onHand.length > 0 && (open
            ? <KeyboardArrowDownRounded sx={{ fontSize: 18, color: TOKENS.ink3 }} />
            : <KeyboardArrowRightRounded sx={{ fontSize: 18, color: TOKENS.ink3 }} />)}
          <QrCode2Rounded sx={{ fontSize: 17, color: TOKENS.ink3 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink }}>
            {onHand.length} sealed {onHand.length === 1 ? 'tub' : 'tubs'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: TOKENS.ink2, fontVariantNumeric: 'tabular-nums' }}>
            {kg(totalKg)}
          </Typography>
        </Stack>
        {onHand.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3, px: 1.25, py: 1.25 }}>
            No sealed tubs on hand.
          </Typography>
        ) : (
          <Collapse in={open} unmountOnExit>
            <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
              <TableHead>
                <TableRow>
                  {['Barcode', 'Owner', 'Batch', 'Where', 'Size'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 10.5, color: TOKENS.ink3, py: 0.5 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {onHand.map((t) => {
                  const onTrolley = t.location_code?.startsWith('TROLLEY');
                  return (
                    <TableRow key={t.unit_code} hover>
                      <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 11.5, py: 0.5 }}>
                        {t.unit_code}
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <TinyChip style={OWNER_STYLE[t.owner] ?? { label: t.owner.toUpperCase(), colour: TOKENS.ink3 }} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink2, py: 0.5, whiteSpace: 'nowrap' }}>
                        {t.batch_no ?? '—'}
                        {t.supplier_batch_code && (
                          <Box component="span" sx={{ color: TOKENS.ink3 }}> / {t.supplier_batch_code}</Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: TOKENS.monoFont, fontSize: 11, py: 0.5,
                        color: onTrolley ? TROLLEY_BLUE : TOKENS.ink2 }}>
                        {t.location_code ?? '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11.5, color: TOKENS.ink2, py: 0.5, whiteSpace: 'nowrap' }}>
                        {kg(t.nominal_kg)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Recent history — a running In / Out / Available ledger (the stock-card view,
// like a Prism "Stock Status" report). One chronological table: In adds,
// Out takes away, Available is the running balance and ties to on-hand at the
// foot. In  = stock that appeared (receipt / opening / stock-take gain);
// Out = stock that left for good (depletion on press / write-off). Trolley
// issue↔return moves stay inside the business — they don't change the balance,
// so they sit in a muted collapsible strip below rather than cluttering it.
// ---------------------------------------------------------------------------

interface RunRow { l: InkLedgerRow; dir: 'in' | 'out'; balance: number; }

function RunningLedger({ ledger, onHandKg }: { ledger: InkLedgerRow[]; onHandKg: number }) {
  const { rows, moves, inKg, outKg } = useMemo(() => {
    // Oldest first so the running balance builds top → bottom.
    const chron = [...ledger].sort((a, b) =>
      a.performed_at < b.performed_at ? -1 : a.performed_at > b.performed_at ? 1 : a.id - b.id);
    const rows: RunRow[] = [];
    const moves: InkLedgerRow[] = [];
    let bal = 0, inKg = 0, outKg = 0;
    for (const l of chron) {
      if (l.from_location != null && l.to_location != null) { moves.push(l); continue; }
      if (l.from_location == null) { bal += l.qty_kg; inKg += l.qty_kg; rows.push({ l, dir: 'in', balance: bal }); }
      else { bal -= l.qty_kg; outKg += l.qty_kg; rows.push({ l, dir: 'out', balance: bal }); }
    }
    return { rows, moves, inKg, outKg };
  }, [ledger]);

  const [showMoves, setShowMoves] = useState(false);
  const HEADS = ['When', 'Type', 'Reference', 'In', 'Out', 'Available'];

  return (
    <Box>
      <SectionLabel>Recent history — In / Out</SectionLabel>
      <Typography sx={{ fontSize: 11.5, color: TOKENS.ink3, mb: 0.75 }}>
        A running stock card: <strong style={{ color: TOKENS.good }}>In</strong> is stock that
        appeared (receipts, opening); <strong style={{ color: TOKENS.bad }}>Out</strong> is stock
        that left for good (used on press, written off). <strong>Available</strong> runs down the
        page and ties to what's on hand.
      </Typography>

      <Box sx={{ border: `1px solid ${TOKENS.line}`, borderRadius: '10px', overflow: 'hidden' }}>
        <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
          <TableHead>
            <TableRow sx={{ bgcolor: TOKENS.lineSoft }}>
              {HEADS.map((h, i) => (
                <TableCell key={h} align={i >= 3 ? 'right' : 'left'}
                  sx={{ fontWeight: 800, fontSize: 10.5, color: TOKENS.ink3, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ l, dir, balance }) => {
              const style = TXN_STYLE[l.txn_type] ?? { label: l.txn_type.toUpperCase(), colour: TOKENS.ink3 };
              const isIn = dir === 'in';
              const where = isIn ? l.to_location : l.from_location;
              return (
                <TableRow key={l.id} hover>
                  <TableCell sx={{ fontSize: 11, color: TOKENS.ink3, whiteSpace: 'nowrap' }}>
                    {dateTimeOf(l.performed_at)}
                  </TableCell>
                  <TableCell><TinyChip style={style} /></TableCell>
                  <TableCell sx={{ fontSize: 11, color: TOKENS.ink2, whiteSpace: 'nowrap' }}>
                    <Box component="span" sx={{ fontFamily: TOKENS.monoFont }}>
                      {where ?? (isIn ? 'outside' : 'OUTSIDE')}
                      {l.batch_no ? ` · ${l.batch_no}` : ''}
                    </Box>
                    {l.reason && (
                      <Box component="span" sx={{ color: TOKENS.ink3, fontStyle: 'italic' }}> — {l.reason}</Box>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: isIn ? TOKENS.good : TOKENS.ink3, whiteSpace: 'nowrap' }}>
                    {isIn ? kg(l.qty_kg) : ''}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                    color: !isIn ? TOKENS.bad : TOKENS.ink3, whiteSpace: 'nowrap' }}>
                    {!isIn ? kg(l.qty_kg) : ''}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: TOKENS.ink2, whiteSpace: 'nowrap' }}>
                    {kg(balance)}
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} sx={{ color: TOKENS.ink3, textAlign: 'center', py: 2 }}>
                No movements yet.
              </TableCell></TableRow>
            )}
          </TableBody>
          {rows.length > 0 && (
            <TableBody>
              <TableRow sx={{ bgcolor: TOKENS.lineSoft, '& td': { borderTop: `2px solid ${TOKENS.line}` } }}>
                <TableCell colSpan={3} sx={{ fontSize: 11, fontWeight: 800, color: TOKENS.ink3, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Totals
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 800, color: TOKENS.good, fontVariantNumeric: 'tabular-nums' }}>
                  {kg(inKg)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 800, color: TOKENS.bad, fontVariantNumeric: 'tabular-nums' }}>
                  {kg(outKg)}
                </TableCell>
                <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 800, color: TOKENS.accent, fontVariantNumeric: 'tabular-nums' }}>
                  {kg(onHandKg)}
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </Box>

      {moves.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}
            onClick={() => setShowMoves((v) => !v)}
            sx={{ cursor: 'pointer', color: TOKENS.ink3, '&:hover': { color: TOKENS.ink2 } }}>
            {showMoves ? <KeyboardArrowDownRounded sx={{ fontSize: 16 }} />
              : <KeyboardArrowRightRounded sx={{ fontSize: 16 }} />}
            <SwapHorizRounded sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>
              {moves.length} internal {moves.length === 1 ? 'move' : 'moves'} (trolley in/out — still ours, balance unchanged)
            </Typography>
          </Stack>
          <Collapse in={showMoves} unmountOnExit>
            <Table size="small" sx={{ mt: 0.5, '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
              <TableBody>
                {moves.map((l) => {
                  const style = TXN_STYLE[l.txn_type] ?? { label: l.txn_type.toUpperCase(), colour: TOKENS.ink3 };
                  return (
                    <TableRow key={l.id} hover>
                      <TableCell sx={{ fontSize: 11, color: TOKENS.ink3, whiteSpace: 'nowrap', py: 0.4 }}>
                        {dateTimeOf(l.performed_at)}
                      </TableCell>
                      <TableCell sx={{ py: 0.4 }}><TinyChip style={style} /></TableCell>
                      <TableCell sx={{ fontFamily: TOKENS.monoFont, fontSize: 11, color: TOKENS.ink2, py: 0.4, whiteSpace: 'nowrap' }}>
                        {l.from_location} <ArrowRightAltRounded sx={{ fontSize: 13, verticalAlign: 'middle', color: TOKENS.ink3 }} /> {l.to_location}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums', py: 0.4, whiteSpace: 'nowrap' }}>
                        {kg(l.qty_kg)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: TOKENS.monoFont, fontSize: 10.5, color: TOKENS.ink3, py: 0.4, whiteSpace: 'nowrap' }}>
                        {l.batch_no ?? '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Goods in / opening balance — same line shape, different intent.
// ---------------------------------------------------------------------------

interface DraftLine {
  ink: InkProduct | null;
  received_date: string;   // ISO yyyy-mm-dd — backdatable for historical receipts
  pack_size: string;       // kg per tub (explicit)
  pack_count: string;      // number of tubs
  unit_cost_per_kg: string;
  supplier_batch_code: string;
}

// Local (not UTC) yyyy-mm-dd, so the default receipt date is the operator's
// calendar day rather than tomorrow's near midnight in Melbourne.
const todayISO = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const blankLine = (): DraftLine => ({
  ink: null, received_date: todayISO(), pack_size: '', pack_count: '',
  unit_cost_per_kg: '', supplier_batch_code: '',
});

/** Fetch the per-tub ZPL for the given batches and download it as ONE .zpl, so
 *  labels are pulled the instant goods are received — closing the "received but
 *  not yet labelled" gap. Becomes one-click auto-print once the LAN bridge lands. */
async function downloadBatchLabels(batchNos: string[]) {
  if (!batchNos.length) return;
  const parts = await Promise.all(batchNos.map((b) => getBatchPailLabels(b)));
  const url = URL.createObjectURL(new Blob([parts.join('')], { type: 'text/plain' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = batchNos.length === 1 ? `${batchNos[0]}-pails.zpl` : 'goods-in-labels.zpl';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function LinesDialog({ mode, onClose, onSaved }: {
  mode: 'receipt' | 'opening'; onClose: () => void; onSaved: () => void;
}) {
  const receipt = mode === 'receipt';
  const [supplier, setSupplier] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([blankLine()]);

  const productsQ = useQuery<InkProduct[]>({
    queryKey: productionKeys.inkProducts({ materialClass: 'all' }),
    queryFn: () => listInkProducts({ materialClass: 'all' }),
    staleTime: 60 * 1000,
  });
  const suppliersQ = useQuery<InkSupplier[]>({
    queryKey: productionKeys.inkSuppliers(false),
    queryFn: () => listInkSuppliers(false),
    staleTime: 60 * 1000,
    enabled: receipt,
  });

  // Total kg is DERIVED — pack size × number of packs — never typed directly.
  const lineKg = (l: DraftLine) => num(l.pack_size) * num(l.pack_count);
  const toItems = (): InkReceiptLine[] => lines
    .filter((l) => l.ink && num(l.pack_size) > 0 && num(l.pack_count) > 0)
    .map((l) => ({
      ink_key: l.ink!.ink_key,
      qty_kg: lineKg(l),
      pack_size_kg: num(l.pack_size),
      pack_count: num(l.pack_count),
      received_at: l.received_date || null,
      unit_cost_per_kg: l.unit_cost_per_kg ? num(l.unit_cost_per_kg) : null,
      supplier_batch_code: l.supplier_batch_code.trim().toUpperCase() || null,
      po_number: receipt ? (poNumber.trim().toUpperCase() || null) : null,
    }));

  const save = useMutation({
    mutationFn: (items: InkReceiptLine[]) => receipt
      ? createInkReceipt({
        supplier_code: supplier || null,
        po_number: poNumber.trim().toUpperCase() || null,
        items,
      })
      : createInkOpeningBalance({ items }),
    // On a receipt, pull the tub labels straight away so they can't be forgotten.
    // Label failure is non-fatal — the stock is already in; you can reprint from
    // the batch detail.
    onSuccess: async (created) => {
      if (receipt && Array.isArray(created)) {
        try {
          await downloadBatchLabels((created as { batch_no: string }[]).map((b) => b.batch_no));
        } catch { /* labels are non-fatal; reprint from the batch row */ }
      }
      onSaved();
    },
  });

  const setLine = (i: number, patch: Partial<DraftLine>) =>
    setLines((prev) => prev.map((l, n) => (n === i ? { ...l, ...patch } : l)));

  const items = toItems();
  const totalKg = items.reduce((t, i) => t + i.qty_kg, 0);
  const totalCost = items.reduce((t, i) => t + i.qty_kg * (i.unit_cost_per_kg ?? 0), 0);
  const valid = items.length > 0 && !(receipt && !supplier);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{receipt ? 'Goods in — receive a delivery' : 'Opening balance'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>
            {receipt
              ? 'One line per ink on the docket. Each line becomes its own batch with our batch number, booked into the store at the cost you enter.'
              : 'The initial inventory load — for what is already on the shelf when the system goes live. Count it, cost it, and book it in once. Everything after this should come in as a real goods-in receipt.'}
          </Typography>

          {receipt && (
            <Stack direction="row" spacing={2}>
              <FormControl size="small" sx={{ width: 260 }}>
                <InputLabel>Supplier</InputLabel>
                <Select label="Supplier" value={supplier} onChange={(e) => setSupplier(String(e.target.value))}>
                  {(suppliersQ.data ?? []).map((s) => (
                    <MenuItem key={s.supplier_code} value={s.supplier_code}>
                      {s.supplier_code} — {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" label="PO number (optional)" value={poNumber}
                onChange={(e) => setPoNumber(up(e.target.value))} sx={{ width: 200 }} />
            </Stack>
          )}

          {save.isError && <Alert severity="error">{apiError(save.error)}</Alert>}

          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)', px: 0.75 } }}>
            <TableHead>
              <TableRow>
                {['Ink', 'Receipt date', 'Pack size', 'Packs', 'Qty (kg)', 'Unit cost /kg', 'Their batch code', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: TOKENS.ink3 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ minWidth: 300 }}>
                    <Autocomplete
                      size="small"
                      options={productsQ.data ?? []}
                      loading={productsQ.isLoading}
                      value={l.ink}
                      onChange={(_e, v) => setLine(i, {
                        ink: v,
                        // Prefill pack size from the SKU's nominal tub size (editable).
                        pack_size: l.pack_size
                          || (v?.pack_size_kg != null ? String(v.pack_size_kg) : ''),
                        unit_cost_per_kg: l.unit_cost_per_kg
                          || (v?.standard_cost_per_kg != null ? String(v.standard_cost_per_kg) : ''),
                      })}
                      getOptionLabel={(o) => `${o.ink_key} — ${o.description?.toUpperCase() ?? ''}`}
                      isOptionEqualToValue={(o, v) => o.ink_key === v.ink_key}
                      renderOption={(props, o) => (
                        <Box component="li" {...props} key={o.ink_key}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Swatch hex={o.hex_colour} coating={o.material_class === 'coating'} />
                            <Typography sx={{ fontFamily: TOKENS.monoFont, fontSize: 12.5, fontWeight: 700,
                              color: o.low_migration ? LMQ_RED : TOKENS.ink }}>
                              {o.ink_key}
                            </Typography>
                            <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>
                              {o.description?.toUpperCase()}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                      renderInput={(params) => <TextField {...params} label="Ink" placeholder="Search…" />}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    <TextField size="small" type="date" value={l.received_date}
                      onChange={(e) => setLine(i, { received_date: e.target.value })}
                      inputProps={{ max: todayISO() }} />
                  </TableCell>
                  <TableCell sx={{ width: 110 }}>
                    <TextField size="small" type="number" value={l.pack_size}
                      onChange={(e) => setLine(i, { pack_size: e.target.value })}
                      placeholder={l.ink?.pack_size_kg != null ? String(l.ink.pack_size_kg) : 'kg'}
                      inputProps={{ step: 0.001, min: 0 }}
                      InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }} />
                  </TableCell>
                  <TableCell sx={{ width: 80 }}>
                    <TextField size="small" type="number" value={l.pack_count}
                      onChange={(e) => setLine(i, { pack_count: e.target.value })}
                      placeholder="#"
                      inputProps={{ step: 1, min: 0 }} />
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: TOKENS.monoFont,
                      color: lineKg(l) > 0 ? TOKENS.ink : TOKENS.ink3 }}>
                      {lineKg(l) > 0 ? kg(lineKg(l)) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ width: 130 }}>
                    <TextField size="small" type="number" value={l.unit_cost_per_kg}
                      onChange={(e) => setLine(i, { unit_cost_per_kg: e.target.value })}
                      inputProps={{ step: 0.01, min: 0 }}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                  </TableCell>
                  <TableCell sx={{ width: 170 }}>
                    <TextField size="small" value={l.supplier_batch_code}
                      onChange={(e) => setLine(i, { supplier_batch_code: up(e.target.value) })}
                      placeholder="THEIR CODE"
                      inputProps={{ style: { fontFamily: TOKENS.monoFont } }} />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 40 }}>
                    <IconButton size="small" disabled={lines.length === 1}
                      onClick={() => setLines((prev) => prev.filter((_x, n) => n !== i))}>
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button size="small" startIcon={<AddRounded />} onClick={() => setLines((p) => [...p, blankLine()])}
              sx={{ color: TOKENS.accent }}>Add line</Button>
            <Stack direction="row" spacing={3}>
              <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>
                {items.length} line{items.length === 1 ? '' : 's'} · <strong>{kg(totalKg)}</strong>
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: TOKENS.ink2 }}>
                Value <strong>{money(totalCost)}</strong>
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || save.isPending}
          onClick={() => save.mutate(items)}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          {receipt ? 'Receive' : 'Load opening stock'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Adjust — a gain or a loss, with a reason that the backend requires.
// ---------------------------------------------------------------------------

const REASONS = ['STOCK TAKE', 'SPOILAGE', 'SPILLAGE'];

export function AdjustDialog({ row, onClose, onSaved }: {
  row: InkStockRow; onClose: () => void; onSaved: () => void;
}) {
  const [direction, setDirection] = useState<'gain' | 'loss'>('loss');
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [cost, setCost] = useState(row.standard_cost_per_kg != null ? String(row.standard_cost_per_kg) : '');
  const [notes, setNotes] = useState('');

  const save = useMutation({
    mutationFn: () => createInkAdjustment({
      ink_key: row.ink_key,
      direction,
      qty_kg: num(qty),
      reason: reason.trim().toUpperCase(),
      unit_cost_per_kg: direction === 'gain' && cost ? num(cost) : null,
      notes: notes.trim().toUpperCase() || null,
    }),
    onSuccess: onSaved,
  });

  const valid = num(qty) > 0 && reason.trim().length > 0;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Swatch hex={row.hex_colour} coating={row.material_class === 'coating'} />
          <Box component="span" sx={{ fontFamily: TOKENS.monoFont }}>Adjust {row.ink_key}</Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3 }}>
            Currently {kg(row.store_kg)} in the store{row.trolley_kg > 0 ? ` (plus ${kg(row.trolley_kg)} out on trolleys)` : ''}.
            Adjustments apply to the store.
          </Typography>

          {save.isError && <Alert severity="error">{apiError(save.error)}</Alert>}

          <ToggleButtonGroup fullWidth size="small" exclusive value={direction}
            onChange={(_e, v: 'gain' | 'loss' | null) => { if (v) setDirection(v); }}>
            <ToggleButton value="loss" sx={{ textTransform: 'none',
              '&.Mui-selected': { bgcolor: `${TOKENS.bad}18`, color: TOKENS.bad, fontWeight: 700 } }}>
              Loss (write off)
            </ToggleButton>
            <ToggleButton value="gain" sx={{ textTransform: 'none',
              '&.Mui-selected': { bgcolor: `${TOKENS.good}18`, color: TOKENS.good, fontWeight: 700 } }}>
              Gain (found)
            </ToggleButton>
          </ToggleButtonGroup>

          <TextField size="small" fullWidth type="number" label="Quantity" value={qty}
            onChange={(e) => setQty(e.target.value)} inputProps={{ step: 0.001, min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }} />

          <Autocomplete
            freeSolo size="small" options={REASONS} value={reason}
            onChange={(_e, v) => setReason(up(String(v ?? '')))}
            onInputChange={(_e, v) => setReason(up(v))}
            renderInput={(params) => (
              <TextField {...params} label="Reason (required)" required
                helperText="Why the count moved — this is the audit trail." />
            )}
          />

          {direction === 'gain' && (
            <TextField size="small" fullWidth type="number" label="Unit cost /kg (optional)" value={cost}
              onChange={(e) => setCost(e.target.value)} inputProps={{ step: 0.01, min: 0 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              helperText="The cost layer created for the found stock." />
          )}

          <TextField size="small" fullWidth multiline minRows={2} label="Notes" value={notes}
            onChange={(e) => setNotes(up(e.target.value))} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || save.isPending} onClick={() => save.mutate()}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>
          Post adjustment
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------

/** Sourcing default for the house (migration 115): who owns the tubs on our floor. */
const SOURCING_MODELS: { value: string; label: string; hint: string }[] = [
  { value: 'purchased', label: 'PURCHASED', hint: 'ours on receipt' },
  { value: 'consignment', label: 'CONSIGNMENT', hint: 'theirs until we use it' },
];
const sourcingLabel = (v: string | null | undefined) =>
  SOURCING_MODELS.find((m) => m.value === v)?.label ?? (v ?? '').toUpperCase();

/** `sourcing_model` rides on the supplier row (migration 115) but the shared
 * API types predate it — widen locally rather than touch productionApi.ts. */
type SupplierRow = InkSupplier & { sourcing_model?: string | null };
type SupplierForm = Pick<InkSupplierUpsert, 'supplier_code' | 'name' | 'lead_time_days'> & { sourcing_model: string };

function SuppliersSection() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<SupplierRow | 'new' | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Active suppliers only — the list endpoint's default. There is no
  // active/inactive concept in this UI any more (demo feedback 2026-09-09).
  const q = useQuery<SupplierRow[]>({
    queryKey: productionKeys.inkSuppliers(false),
    queryFn: () => listInkSuppliers(false),
    staleTime: 60 * 1000,
  });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['production', 'ink-suppliers'] }); };

  // The backend keeps (deactivates) rather than deletes a supplier that is
  // referenced — either way it leaves this list, so just say what happened.
  const del = useMutation({
    mutationFn: (code: string) => deleteInkSupplier(code),
    onSuccess: (res, code) => {
      setError(null);
      setNote(res.action === 'deleted'
        ? `${code} deleted.`
        : `${code} removed from the list — kept on file because products or batches reference it.`);
      invalidate();
    },
    onError: (err) => { setNote(null); setError(apiError(err)); },
  });

  return (
    <>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontSize: 13, color: TOKENS.ink3 }}>
          Who we buy ink from. Lead time drives how early a below-reorder SKU needs a PO raised.
        </Typography>
        <Button size="small" variant="contained" startIcon={<AddRounded />} onClick={() => setDialog('new')}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>Add supplier</Button>
      </Stack>

      {note && <Alert severity="info" onClose={() => setNote(null)} sx={{ mb: 1.5 }}>{note}</Alert>}
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>{error}</Alert>}

      {q.isLoading ? <Loading /> : q.isError ? (
        <Alert severity="error">Couldn’t load suppliers: {apiError(q.error)}</Alert>
      ) : (
        <Box sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
          <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(0,0,0,0.06)' } }}>
            <TableHead>
              <TableRow>
                {['Code', 'Name', 'Sourcing', 'Lead time', 'Products', ''].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11.5, color: TOKENS.ink3 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(q.data ?? []).map((s) => (
                <TableRow key={s.supplier_code} hover>
                  <TableCell sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700 }}>{s.supplier_code}</TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{s.name?.toUpperCase()}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: TOKENS.ink2 }}>{sourcingLabel(s.sourcing_model)}</TableCell>
                  <TableCell sx={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                    {s.lead_time_days == null ? '—' : `${s.lead_time_days} day${s.lead_time_days === 1 ? '' : 's'}`}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{s.product_count ?? 0}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setDialog(s)}><EditRounded fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => {
                      if (window.confirm(`Remove supplier ${s.supplier_code}?`)) {
                        del.mutate(s.supplier_code);
                      }
                    }}><DeleteOutlineRounded fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(q.data ?? []).length === 0 && (
                <TableRow><TableCell colSpan={6} sx={{ color: TOKENS.ink3, textAlign: 'center', py: 3 }}>
                  No suppliers yet — add one so goods-in can be attributed.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      {dialog && (
        <SupplierDialog initial={dialog === 'new' ? null : dialog}
          onClose={() => setDialog(null)}
          onSaved={() => { setDialog(null); invalidate(); }} />
      )}
    </>
  );
}

/** Code · name · sourcing model · lead time. Contact details, account ref and
 * notes were dropped from the dialog (demo feedback 2026-09-09) — the columns
 * remain on the table, untouched. */
function SupplierDialog({ initial, onClose, onSaved }: {
  initial: SupplierRow | null; onClose: () => void; onSaved: () => void;
}) {
  const editing = !!initial;
  const [f, setF] = useState<SupplierForm>({
    supplier_code: initial?.supplier_code ?? '',
    name: initial?.name ?? '',
    sourcing_model: initial?.sourcing_model ?? 'purchased',
    lead_time_days: initial?.lead_time_days ?? null,
  });
  const save = useMutation({
    mutationFn: (p: SupplierForm) => upsertInkSupplier(p),
    onSuccess: onSaved,
  });
  const valid = f.supplier_code.trim().length > 0 && f.name.trim().length > 0;
  const sourcing = SOURCING_MODELS.find((m) => m.value === f.sourcing_model);

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {editing ? `Edit supplier ${initial!.supplier_code}` : 'Add supplier'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {save.isError && <Alert severity="error">{apiError(save.error)}</Alert>}
          <TextField label="Supplier code" value={f.supplier_code} disabled={editing} size="small" fullWidth
            onChange={(e) => setF({ ...f, supplier_code: up(e.target.value) })}
            helperText="e.g. FLINT, HUBER. Fixed once created." />
          <TextField label="Name" value={f.name} size="small" fullWidth
            onChange={(e) => setF({ ...f, name: up(e.target.value) })} />
          <FormControl size="small" fullWidth>
            <InputLabel>Sourcing model</InputLabel>
            <Select label="Sourcing model" value={f.sourcing_model}
              onChange={(e) => setF({ ...f, sourcing_model: String(e.target.value) })}>
              {SOURCING_MODELS.map((m) => (
                <MenuItem key={m.value} value={m.value}>{m.label} — {m.hint}</MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {sourcing?.value === 'consignment'
                ? 'They own the tubs on our floor until we open them. Every series and ink from this house inherits this.'
                : 'Ours on receipt. Every series and ink from this house inherits this.'}
            </FormHelperText>
          </FormControl>
          {/* Wide enough that the value stays readable once the spinner appears. */}
          <TextField label="Lead time" type="number" size="small" sx={{ width: 180 }}
            value={f.lead_time_days ?? ''}
            onChange={(e) => setF({ ...f, lead_time_days: e.target.value === '' ? null : num(e.target.value) })}
            inputProps={{ step: 1, min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
            helperText="Order to delivery" />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || save.isPending}
          onClick={() => save.mutate({
            ...f,
            supplier_code: f.supplier_code.trim().toUpperCase(),
            name: f.name.trim().toUpperCase(),
          })}
          sx={{ bgcolor: TOKENS.accent, '&:hover': { bgcolor: TOKENS.accentStrong } }}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
